import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from "lucide-react";
import { cloneElement, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode, useMemo } from "react";
import { Button } from "../../components/Button/Button";
import { Pagination, type PaginationProps } from "../../components/Pagination/Pagination";
import { Skeleton } from "../../components/Skeleton/Skeleton";

export type DataTableSortDirection = "asc" | "desc";

export interface DataTableSort {
  key: string;
  direction: DataTableSortDirection;
}

export interface DataTableColumn<T> {
  key: string;
  header: string;

  align?: "left" | "right";
  render: (row: T) => ReactNode;

  /** Show a sort control in the header. Implied when `sortValue` is set. */
  sortable?: boolean;
  /** Sort rows in the table by this value. Omit it to sort server-side (rows arrive pre-sorted). */
  sortValue?: (row: T) => string | number | null | undefined;

  /** Cell holds its own controls (toggle, menu, link): clicks and keys inside it don't trigger `onRowClick`. */
  interactive?: boolean;

  /**
   * Stacked layout below `md`: `primary` is the row's title (no label, full width; defaults to the first
   * column), `hidden` drops the cell, `default` renders as a "Header: value" line.
   */
  mobile?: "primary" | "default" | "hidden";
}

export interface DataTableLoadMore {
  onLoadMore: () => void;
  hasMore: boolean;
  loading?: boolean;
  label?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;

  /** Accessible name for the table. */
  "aria-label"?: string;

  /** Show skeleton rows instead of data or the empty state. */
  loading?: boolean;
  skeletonRows?: number;

  emptyIcon?: ReactElement<{ size?: number; className?: string }>;
  emptyMessage?: string;
  /** Replaces the built-in empty message (e.g. an `EmptyState` with an action). */
  empty?: ReactNode;

  onRowClick?: (row: T) => void;

  renderActions?: (row: T) => ReactNode;
  /** Header for the actions column. */
  actionsLabel?: string;

  /** Controlled sort. Pair with `onSortChange`. */
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;

  /** Numbered pagination footer. */
  pagination?: PaginationProps;
  /** "Load more" footer (cursor/infinite lists). */
  loadMore?: DataTableLoadMore;
}

const HEADER_CELL =
  "whitespace-nowrap px-4 py-2 [font:var(--type-eyebrow)] text-content-tertiary uppercase tracking-wide";
const CELL = "md:table-cell md:px-4 md:py-2 md:align-middle";
const STACKED_LABEL =
  "before:shrink-0 before:text-content-tertiary before:content-[attr(data-label)] md:before:content-none";

function stopRowActivation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

function compareValues(a: string | number | null | undefined, b: string | number | null | undefined) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function SortIcon({ direction }: Readonly<{ direction: DataTableSortDirection | null }>) {
  if (direction === "asc") return <ArrowUp size={12} aria-hidden="true" />;
  if (direction === "desc") return <ArrowDown size={12} aria-hidden="true" />;
  return <ArrowUpDown size={12} aria-hidden="true" className="opacity-50" />;
}

function HeaderCell<T>({
  column,
  sort,
  onSortChange,
}: Readonly<{ column: DataTableColumn<T>; sort?: DataTableSort; onSortChange?: (sort: DataTableSort) => void }>) {
  const align = column.align === "right" ? "text-right" : "text-left";
  const isSortable = Boolean(onSortChange) && (column.sortable || Boolean(column.sortValue));
  const direction = sort?.key === column.key ? sort.direction : null;

  if (!isSortable) {
    return (
      <th scope="col" className={`${HEADER_CELL} ${align}`}>
        {column.header}
      </th>
    );
  }

  const next: DataTableSort = { key: column.key, direction: direction === "asc" ? "desc" : "asc" };
  let ariaSort: "ascending" | "descending" | "none" = "none";
  if (direction) ariaSort = direction === "asc" ? "ascending" : "descending";

  return (
    <th scope="col" aria-sort={ariaSort} className={`${HEADER_CELL} ${align}`}>
      <button
        type="button"
        onClick={() => onSortChange?.(next)}
        className={[
          "inline-flex items-center gap-1 rounded-sm uppercase tracking-wide hover:text-content-primary",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus",
          direction ? "text-content-primary" : "",
          column.align === "right" ? "flex-row-reverse" : "",
        ].join(" ")}
      >
        {column.header}
        <SortIcon direction={direction} />
      </button>
    </th>
  );
}

function bodyCellClass<T>(column: DataTableColumn<T>, isPrimary: boolean) {
  const align = column.align === "right" ? "md:text-right" : "md:text-left";
  if (column.mobile === "hidden") return `hidden px-4 py-2 ${align} md:table-cell md:align-middle`;
  if (isPrimary) return `block py-0.5 font-medium text-content-primary md:font-normal ${CELL} ${align}`;
  return `flex justify-between gap-4 py-0.5 text-right ${STACKED_LABEL} ${CELL} ${align}`;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  "aria-label": ariaLabel,
  loading = false,
  skeletonRows = 5,
  emptyIcon,
  emptyMessage = "Nothing here yet",
  empty,
  onRowClick,
  renderActions,
  actionsLabel = "Actions",
  sort,
  onSortChange,
  pagination,
  loadMore,
}: Readonly<DataTableProps<T>>) {
  const colCount = columns.length + (renderActions ? 1 : 0) + (onRowClick ? 1 : 0);
  const primaryKey = (columns.find((c) => c.mobile === "primary") ?? columns.find((c) => c.mobile !== "hidden"))?.key;

  const sortedRows = useMemo(() => {
    const sortColumn = sort ? columns.find((c) => c.key === sort.key) : undefined;
    const sortValue = sortColumn?.sortValue;
    if (!sort || !sortValue) return rows;
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareValues(sortValue(a), sortValue(b)));
  }, [rows, columns, sort]);

  const handleRowKeyDown = (row: T) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const renderBody = () => {
    if (loading) {
      return Array.from({ length: skeletonRows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows have no identity.
        <tr key={`skeleton-${i}`} className="block px-4 py-3 md:table-row md:p-0">
          {columns.map((column) => (
            <td key={column.key} className={`block py-1 ${CELL}`}>
              <Skeleton className="h-3.5 w-3/4" />
            </td>
          ))}
          {colCount > columns.length && <td className="hidden md:table-cell" colSpan={colCount - columns.length} />}
        </tr>
      ));
    }

    if (sortedRows.length === 0) {
      return (
        <tr className="block md:table-row">
          <td className="block px-4 py-12 text-center md:table-cell" colSpan={colCount}>
            {empty ?? (
              <>
                {emptyIcon && (
                  <div className="mb-3 flex justify-center text-content-quaternary">
                    {cloneElement(emptyIcon, { size: 32 })}
                  </div>
                )}
                <p className="text-content-tertiary">{emptyMessage}</p>
              </>
            )}
          </td>
        </tr>
      );
    }

    return sortedRows.map((row) => (
      <tr
        key={rowKey(row)}
        className={[
          "block px-4 py-3 transition-colors hover:bg-surface-secondary md:table-row md:p-0",
          onRowClick
            ? "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus"
            : "",
        ].join(" ")}
        tabIndex={onRowClick ? 0 : undefined}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        onKeyDown={onRowClick ? handleRowKeyDown(row) : undefined}
      >
        {columns.map((column) => (
          <td
            key={column.key}
            data-label={column.header}
            className={bodyCellClass(column, column.key === primaryKey)}
            onClick={column.interactive ? stopRowActivation : undefined}
            onKeyDown={column.interactive ? stopRowActivation : undefined}
          >
            {column.render(row)}
          </td>
        ))}
        {renderActions && (
          <td
            className={`flex justify-end pt-1 ${CELL} md:text-right`}
            onClick={stopRowActivation}
            onKeyDown={stopRowActivation}
          >
            {renderActions(row)}
          </td>
        )}
        {onRowClick && (
          <td className="hidden px-4 py-2 text-right md:table-cell md:align-middle">
            <ChevronRight size={14} aria-hidden="true" className="inline-block text-content-tertiary" />
          </td>
        )}
      </tr>
    ));
  };

  const showLoadMore = loadMore?.hasMore && !loading;
  const showPagination = pagination && pagination.pageCount > 1 && !loading;

  return (
    <div>
      <div
        role="region"
        aria-label={ariaLabel}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a horizontally scrollable region must be keyboard-reachable (WCAG 2.1.1, axe scrollable-region-focusable).
        tabIndex={0}
        className="rounded-sm focus-visible:outline-2 focus-visible:outline-border-focus md:overflow-x-auto"
      >
        <table
          aria-label={ariaLabel}
          aria-busy={loading || undefined}
          className="block w-full text-[13px] leading-[18px] md:table"
        >
          <thead className="sr-only md:not-sr-only md:table-header-group">
            <tr className="border-b border-border-default">
              {columns.map((column) => (
                <HeaderCell key={column.key} column={column} sort={sort} onSortChange={onSortChange} />
              ))}
              {renderActions && (
                <th scope="col" className={`${HEADER_CELL} text-right`}>
                  {actionsLabel}
                </th>
              )}
              {onRowClick && <td className="px-4 py-2" />}
            </tr>
          </thead>
          <tbody className="block divide-y divide-border-default md:table-row-group">{renderBody()}</tbody>
        </table>
      </div>
      {showPagination && (
        <div className="border-t border-border-default p-3">
          <Pagination {...pagination} />
        </div>
      )}
      {showLoadMore && (
        <div className="flex justify-center border-t border-border-default p-3">
          <Button variant="ghost" size="sm" loading={loadMore.loading} onClick={loadMore.onLoadMore}>
            {loadMore.label ?? "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
